import * as React from 'react'

import {
  Tooltip as ShadTooltip,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipTrigger,
} from '@ps/ds/ui/tooltip'

export interface TooltipProps {
  /** The content to show in the tooltip */
  content: React.ReactNode
  /** The element that triggers the tooltip on hover */
  children: React.ReactNode
  /** Side of the trigger to render the tooltip */
  side?: 'top' | 'right' | 'bottom' | 'left'
  /** Alignment of the tooltip relative to the trigger */
  align?: 'start' | 'center' | 'end'
  /** The distance in pixels from the trigger */
  sideOffset?: number
  /** The delay in milliseconds before the tooltip appears */
  delayDuration?: number
  /** Whether to skip the delay when opening (useful for nested tooltips) */
  skipDelayDuration?: number
  /** Additional class name for the tooltip content */
  className?: string
  /** Whether the tooltip should be open by default */
  defaultOpen?: boolean
  /** Controlled open state */
  open?: boolean
  /** Callback when the open state changes */
  onOpenChange?: (open: boolean) => void
}

export function Tooltip({
  content,
  children,
  side = 'top',
  align = 'center',
  sideOffset = 4,
  delayDuration = 0,
  skipDelayDuration = 300,
  className,
  defaultOpen,
  open,
  onOpenChange,
}: TooltipProps) {
  return (
    <TooltipProvider delayDuration={delayDuration} skipDelayDuration={skipDelayDuration}>
      <ShadTooltip defaultOpen={defaultOpen} open={open} onOpenChange={onOpenChange}>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent side={side} align={align} sideOffset={sideOffset} className={className}>
          {content}
        </TooltipContent>
      </ShadTooltip>
    </TooltipProvider>
  )
}

Tooltip.displayName = 'Tooltip'

export { TooltipProvider, TooltipTrigger, TooltipContent, TooltipPortal }
